import { useDataCatalog } from "@/composable/data-catalog";
import {
	accelerationUnits,
	DataType,
	overpressureUnits,
	soundUnits,
	velocityUnits,
	VibrationSubType as vst,
} from "@soft-db/data-formatter";
import { Show, useModelValue, useRefObjectSync } from "@soft-db/soft-utils";
import { SoftSelect } from "@soft-db/ui";
import { computed, defineComponent, PropType } from "vue";
import { defaultEventListDataOptions, EventListDataOptions } from "./event-list-data-options";

export default defineComponent({
	name: "EventListPluginDataConfig",
	props: {
		modelValue: {
			type: Object as PropType<EventListDataOptions>,
			default: () => ({ ...defaultEventListDataOptions }),
		},
	},
	setup(props) {
		const { t } = useI18n();

		const valueModel = useModelValue(props);

		const type = useRefObjectSync(valueModel, "type");
		const subType = useRefObjectSync(valueModel, "subType");
		const unit = useRefObjectSync(valueModel, "unit");
		const oplUnit = useRefObjectSync(valueModel, "oplUnit");

		watch(type, (type, oldType) => {
			if (type && type !== oldType) {
				if (type === DataType.SOUND) {
					subType.value = "";
					unit.value = "dB";
				} else if (type === DataType.VIBRATION) {
					subType.value = vst.VELOCITY;
					unit.value = "mm/s";
				}
			} else unit.value = "";
		});
		watch(subType, (subType, oldSubType) => {
			if (subType && subType !== oldSubType) {
				if (subType === vst.ACCELERATION) unit.value = "mm/s²";
				if (subType === vst.VELOCITY) unit.value = "mm/s";
			}
		});

		const hasSubType = computed(() => type.value === DataType.VIBRATION);

		const { generateMenuEntries, EventType, VibrationSubType } = useDataCatalog();
		const events = generateMenuEntries(EventType);
		const subTypes = generateMenuEntries(VibrationSubType);
		const units = computed(() => {
			if (type.value === "sound") return [...soundUnits];
			if (type.value === "vibration") {
				if (subType.value === vst.ACCELERATION) return [...accelerationUnits];
				if (subType.value === vst.VELOCITY) return [...velocityUnits];
			}

			return [null];
		});
		const oplUnits = computed(() => [...overpressureUnits]);

		return () => (
			<div class="m-4">
				<div class="text-subtitle-2">{t("dashboard.eventSettings")}</div>
				<div class="auto-grid grid gap-4">
					<SoftSelect
						v-model={type.value}
						label={t("dashboard.eventType")}
						items={events}
						item-text="name"
						item-value="value"
					/>
					<Show when={hasSubType.value}>
						<SoftSelect
							v-model={subType.value}
							label={t("dashboard.vibrationType")}
							items={subTypes}
							item-text="name"
							item-value="value"
						/>
					</Show>
					<SoftSelect
						v-model={unit.value}
						label={t("dashboard.unit")}
						items={units.value}
						disabled={!type.value}
					/>
				</div>
				<div class="grid grid-cols-1 gap-4">
					<SoftSelect
						v-model={oplUnit.value}
						label={t("dashboard.units.overpressure")}
						items={oplUnits.value}
					/>
				</div>
			</div>
		);
	},
});
